import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import {
	remixReadyMessages,
	RemixReadyToast,
} from '@atlassian/malleable-ui-components/remix/remix-ready-toast';
import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
} from '@atlassian/malleable-ui-components/types';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReady } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReady';

import { makeAddRemixHistoryEntryCommand } from '../../editor-commands/addRemixHistoryEntry';
import { makeAddRemixHistoryStackEntryCommand } from '../../editor-commands/addRemixHistoryStackEntry';
import { clearRemixThinkingDecoration } from '../../editor-commands/clear-remix-thinking-decoration';
import { setRemixThinkingComplete } from '../../editor-commands/set-remix-thinking-complete';
import { setShowLocationPickerEditorCommand } from '../../editor-commands/set-show-location-picker';
import { onStartRemixThinking } from '../../editor-commands/start-remix-thinking';
import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';
import { MauiEmbedFinalizeStatus } from '../../pm-plugins/maui-diagram-paste-plugin';
import { getRemixThinkingWidgetContainer } from '../../pm-plugins/remix-thinking/remix-thinking-decoration';
import { useIsThinkingWidgetInViewport } from '../hooks/useIsThinkingWidgetInViewport';
import {
	cancelRemixThinking,
	getAbortRequest,
	onRemixThinkingCompleteAnimationFinished,
	registerAbortRequest,
} from '../utils/remix-state';

const styles = cssMap({
	// Floats the ready bar at the bottom-centre of the viewport, matching the
	// position of the other floating thinking/ready bars (and the legacy toast).
	floatingReadyBar: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: 'fit-content',
		// 400px matches the shared Rovo floating ready bar design minimum; no ADS sizing token maps to it.
		minWidth: '400px',
		zIndex: 600,
	},
});

interface RemixContentProps {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
}

export const RemixContent = ({ api, config }: RemixContentProps): React.JSX.Element | null => {
	const { createRemixContentComponent } = config;
	const { editorView } = useContext(ReactEditorViewContext);
	const { formatMessage } = useIntl();

	const isViewMode = useSharedPluginStateWithSelector(
		api,
		['editorViewMode'],
		(states) => states.editorViewModeState?.mode === 'view',
	);

	const isModalOpen = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isRemixModalOpen || false,
	);

	const isThinking = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isThinking || false,
	);

	const isComplete = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isComplete || false,
	);

	const remixModalMetadata = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixModalMetadata,
	);

	const remixSource = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixSource,
	);

	const showLocationPicker = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.showLocationPicker || false,
	);

	const isUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isUpdate || false,
	);

	const historyEntryForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.historyEntryForUpdate,
	);

	const dynamicUiSubtypeForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.dynamicUiSubtypeForUpdate,
	);

	const savedSelection = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.savedSelection,
	);

	// Start Remix experience for floating toolbar entry point (can't use hooks in toolbar config).
	const experienceTracker = useAIMateExperienceTracker();
	const prevIsModalOpen = useRef(false);
	useEffect(() => {
		if (!prevIsModalOpen.current && isModalOpen && remixSource === 'floatingToolbar') {
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'floatingToolbar',
					product: 'confluence',
				},
			});
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen, remixSource, experienceTracker]);
	const Component = useMemo(() => createRemixContentComponent(), [createRemixContentComponent]);
	const isUnifiedRovoThinkingBarsEnabled = fg('use_unified_rovo_thinking_bars_for_remix');

	const handleModalClose = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.closeRemixModal());
	}, [api]);

	const onStartThinking = useCallback(
		(options?: { contentType?: string }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				onStartRemixThinking({ api, options });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
				api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
			}
		},
		[api],
	);

	const onStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				// Abort before cancelling so send-message-action.ts fires the paired
				// aiInteraction initiated + dismissed events via AbortError.
				getAbortRequest(api)?.();
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const onDismissThinkingUi = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const onRegisterAbortRequest = useCallback(
		(abortFn: () => void) => registerAbortRequest(api, abortFn),
		[api],
	);

	const clearThinkingDecoration = useCallback(() => {
		api?.core.actions.execute(clearRemixThinkingDecoration());
	}, [api]);

	const replaceWhiteboardEmbed = useCallback(
		(options: { contentId: string; finalizedUrl: string; localId?: string }) => {
			api?.core.actions.execute(api?.malleableUi.commands.replaceWhiteboardEmbed(options));
		},
		[api],
	);

	// Maps the MAUI-side string status to the editor's finalize-status enum.
	const setMauiEmbedFinalizeStatus = useCallback(
		(options: { contentId: string; status: 'suppressed' | 'needs-finalize' }) => {
			api?.core.actions.execute(
				api?.malleableUi.commands.setMauiEmbedFinalizeStatus({
					contentId: options.contentId,
					status:
						options.status === 'suppressed'
							? MauiEmbedFinalizeStatus.CHECKING
							: MauiEmbedFinalizeStatus.NEEDS_FINALIZE,
				}),
			);
		},
		[api],
	);

	const setThinkingComplete = useCallback(
		(isComplete: boolean) => {
			const shouldSkipInlineCompletionAnimation = isUnifiedRovoThinkingBarsEnabled;

			// this is to clear the inline-Rovo streaming target when the remix is complete
			if (isComplete) {
				api?.aiExperience?.actions?.endInlineRovoStreaming?.();
			}
			api?.core.actions.execute(setRemixThinkingComplete(isComplete));
			if (isComplete && shouldSkipInlineCompletionAnimation) {
				onRemixThinkingCompleteAnimationFinished({ api });
			}
		},
		[api, isUnifiedRovoThinkingBarsEnabled],
	);

	const finishRemixThinking = useCallback(() => {
		onRemixThinkingCompleteAnimationFinished({ api });
	}, [api]);

	const _onRemixThinkingCompleteAnimationFinished = useCallback(() => {
		onRemixThinkingCompleteAnimationFinished({ api });
	}, [api]);

	const setShowLocationPicker = useCallback(
		(showLocationPicker: boolean) => {
			api?.core.actions.execute(setShowLocationPickerEditorCommand(showLocationPicker));
		},
		[api],
	);

	const addHistoryEntry = useCallback(
		(options: AddRemixHistoryEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryEntryCommand(options));
		},
		[api],
	);

	const addHistoryStackEntry = useCallback(
		(options: AddRemixHistoryStackEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryStackEntryCommand(options));
		},
		[api],
	);

	// Bridges the plugin's `trackVariantUpdateComplete` command into the
	// remix flow; `useOnComplete` fires it once the FE insertion settles.
	const onVariantUpdateComplete = useCallback(
		(id: string) => {
			if (!api?.malleableUi?.commands.trackVariantUpdateComplete) {
				return;
			}
			api.core.actions.execute(api.malleableUi.commands.trackVariantUpdateComplete({ id }));
		},
		[api],
	);

	const isThinkingWidgetInViewport = useIsThinkingWidgetInViewport({ editorView, isComplete });

	const handleReadyToastView = useCallback(() => {
		if (editorView) {
			const container = getRemixThinkingWidgetContainer(editorView);
			if (fg('use_unified_rovo_thinking_bars_for_remix')) {
				const target =
					(savedSelection?.from !== undefined && editorView.nodeDOM(savedSelection.from)) ||
					container ||
					(() => {
						try {
							if (!savedSelection?.to) {
								return null;
							}
							const resolved = editorView.state.doc.resolve(savedSelection.to);
							const anchorPos = resolved.depth >= 1 ? resolved.after(1) : savedSelection.to;
							const domAtPos = editorView.domAtPos(anchorPos);
							return domAtPos.node instanceof HTMLElement
								? domAtPos.node
								: domAtPos.node.parentElement;
						} catch {
							return null;
						}
					})();
				if (target instanceof HTMLElement) {
					target.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			} else if (fg('platform_editor_ai_remix_toolbar')) {
				// for insertion, the next sibling should be the inserted content
				// but after insertion, the decoration is re-rendered after the insertion
				// so the inserted content become the previous sibling
				// for update, the previous sibling is the updated content
				const sibling = container?.previousElementSibling;
				sibling?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			} else {
				// the next sibling is the inserted content
				const nextSibling = container?.nextSibling;
				if (nextSibling instanceof HTMLElement) {
					nextSibling.scrollIntoView({ behavior: 'smooth', block: 'center' });
				}
			}
		}
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [editorView, savedSelection?.from, savedSelection?.to, setThinkingComplete]);

	const handleReadyToastClose = useCallback(() => {
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [setThinkingComplete]);

	// handle escape key to stop remix thinking
	useEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}

		if (!isThinking || !editorView) {
			return;
		}

		// focus editor first
		if (!editorView.hasFocus()) {
			editorView.focus();
		}

		return bind(editorView.dom, {
			type: 'keydown',
			listener: (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					e.stopPropagation();

					if (fg('platform_editor_ai_remix_toolbar')) {
						// Abort before cancelling so send-message-action.ts fires the paired
						// aiInteraction initiated + dismissed events via AbortError.
						getAbortRequest(api)?.();
						cancelRemixThinking({ api });
					} else {
						if (
							expValEqualsNoExposure(
								'cc_page_experiences_editor_image_generation',
								'isEnabled',
								true,
							)
						) {
							// If a pre-supplied userPrompt was cancelled (e.g. image generation flow),
							// call the onCancel callback instead of reopening the remix modal.
							const currentMetadata =
								api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
							const cancelledPrompt = currentMetadata?.userPrompt;
							const onCancel = currentMetadata?.onCancel;
							if (cancelledPrompt && onCancel) {
								onStopThinking({ reopenModal: false });
								onCancel();
								return;
							}
						}
						onStopThinking({
							reopenModal: fg('platform_editor_ai_remix_toolbar') ? !isUpdate : true,
						});
					}
				}
			},
		});
	}, [isThinking, onStopThinking, editorView, api?.malleableUi.sharedState, isUpdate, api]);

	// When the remix modal overlay is open (isModalOpen),
	// set the editor user intent to 'overlayOpen' so the floating toolbar is suppressed
	// (it would otherwise appear above the overlay in Jira).
	// We intentionally do NOT suppress during the thinking-only state
	// — the user may still interact with the editor while the AI is thinking.
	// Reset to 'default' when the modal closes or the component unmounts.
	useLayoutEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (!isModalOpen) {
			if (api?.userIntent?.sharedState.currentState()?.currentUserIntent === 'overlayOpen') {
				api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
			}
			return;
		}
		api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('overlayOpen'));
		return () => {
			api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
		};
	}, [isModalOpen, api]);

	if (!api || !Component) {
		return null;
	}

	const selectedContext = api?.aiExperience?.actions.getAISelectionContext();
	if (!selectedContext) {
		return null;
	}

	// when the remix isComplete and
	// - the modal is not open because isComplete may not be reset to false when the modal is open
	// - not thinking because we need to show the thinking bar when isThinking and isComplete
	//   - after onCompleteAnimationFinished, isThinking will be false and only isComplete will be true
	// we need to show the ready toast
	if (
		!isViewMode &&
		isComplete &&
		!isThinking &&
		!isModalOpen &&
		fg('platform_editor_ai_remix_in_editor_loading')
	) {
		// When the unified bars are on, surface the shared Rovo "ready" state as a
		// floating bar at the bottom of the viewport (closing the inline thinking
		// bar). Like the legacy toast, only show it when the inline widget is
		// scrolled out of view so it can take the user back to their remix.
		if (fg('use_unified_rovo_thinking_bars_for_remix')) {
			if (isThinkingWidgetInViewport) {
				return null;
			}
			return (
				<Box xcss={styles.floatingReadyBar}>
					<RovoThinkingBarWrapper>
						<RovoThinkingReady
							onView={handleReadyToastView}
							onClose={handleReadyToastClose}
							readyText={formatMessage(
								isUpdate ? remixReadyMessages.updateReady : remixReadyMessages.remixReady,
							)}
						/>
					</RovoThinkingBarWrapper>
				</Box>
			);
		}
		// Legacy toast only shows when the inline widget is scrolled out of view.
		if (isThinkingWidgetInViewport) {
			return null;
		}
		return (
			<RemixReadyToast
				isUpdate={fg('platform_editor_ai_remix_toolbar') ? isUpdate : undefined}
				onView={handleReadyToastView}
				onClose={handleReadyToastClose}
			/>
		);
	}

	// When the flag is ON, only show when isModalOpen, isThinking, or showLocationPicker — this
	// is the correct gating behaviour for the new loading path.
	// When the flag is OFF (legacy path), always show — EXCEPT when a pre-supplied userPrompt
	// is present (image-gen flow) and the flow is done (neither isModalOpen nor isThinking),
	// to prevent the option-picker from flashing on cancellation.
	const hasPreSuppliedPrompt = !!remixModalMetadata?.userPrompt;
	const canShow =
		isModalOpen ||
		isThinking ||
		showLocationPicker ||
		(!fg('platform_editor_ai_remix_in_editor_loading') && !hasPreSuppliedPrompt);

	return canShow ? (
		<Component
			isViewMode={fg('platform_editor_ai_remix_toolbar') ? isViewMode : undefined}
			isModalOpen={isModalOpen}
			isThinking={isThinking}
			isUpdate={fg('platform_editor_ai_remix_toolbar') ? isUpdate : undefined}
			historyEntryForUpdate={
				fg('platform_editor_ai_remix_toolbar') ? historyEntryForUpdate : undefined
			}
			dynamicUiSubtypeForUpdate={
				fg('platform_editor_ai_remix_toolbar') ? dynamicUiSubtypeForUpdate : undefined
			}
			isComplete={fg('platform_editor_ai_remix_in_editor_loading') ? isComplete : undefined}
			handleModalClose={handleModalClose}
			remixSource={remixSource}
			selectedContext={selectedContext}
			editorApi={api}
			clearThinkingDecoration={
				fg('platform_editor_ai_remix_in_editor_loading') ? clearThinkingDecoration : undefined
			}
			replaceWhiteboardEmbed={
				fg('platform_editor_ai_remix_in_editor_loading') ? replaceWhiteboardEmbed : undefined
			}
			setMauiEmbedFinalizeStatus={
				fg('platform_editor_ai_remix_in_editor_loading') ? setMauiEmbedFinalizeStatus : undefined
			}
			setThinkingComplete={
				fg('platform_editor_ai_remix_in_editor_loading') ? setThinkingComplete : undefined
			}
			finishRemixThinking={
				!isUnifiedRovoThinkingBarsEnabled && fg('platform_editor_ai_remix_in_editor_loading')
					? finishRemixThinking
					: undefined
			}
			onStartThinking={onStartThinking}
			onDismissThinkingUi={onDismissThinkingUi}
			onStopThinking={onStopThinking}
			onRegisterAbortRequest={onRegisterAbortRequest}
			onRemixThinkingCompleteAnimationFinished={
				fg('platform_editor_ai_remix_toolbar')
					? _onRemixThinkingCompleteAnimationFinished
					: undefined
			}
			showLocationPicker={
				fg('platform_editor_ai_remix_in_editor_loading') ? showLocationPicker : false
			}
			setShowLocationPicker={
				fg('platform_editor_ai_remix_in_editor_loading') ? setShowLocationPicker : () => {}
			}
			addHistoryEntry={fg('platform_editor_ai_remix_toolbar') ? addHistoryEntry : () => {}}
			addHistoryStackEntry={
				fg('platform_editor_ai_remix_toolbar') ? addHistoryStackEntry : () => {}
			}
			onVariantUpdateComplete={
				fg('platform_editor_ai_remix_toolbar') ? onVariantUpdateComplete : undefined
			}
			remixModalMetadata={remixModalMetadata}
			hiddenOptions={config.hiddenOptions}
		/>
	) : null;
};
